/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/* eslint-disable @angular-eslint/no-empty-lifecycle-method */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-unsafe-return */
import { CommonModule } from "@angular/common";
import {
  Component,
  OnInit,
  OnDestroy,
  signal,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  inject,
} from "@angular/core";
import { Subject, takeUntil } from "rxjs";

// PrimeNG
import { ButtonModule } from "primeng/button";
import { DialogModule } from "primeng/dialog";
import { DividerModule } from "primeng/divider";
import { ProgressBarModule } from "primeng/progressbar";
import { TableModule } from "primeng/table";
import { TagModule } from "primeng/tag";
import { TooltipModule } from "primeng/tooltip";
import { DynamicDialogConfig, DynamicDialogRef } from "primeng/dynamicdialog";

import {
  CouponsService,
  CouponImportResult,
} from "../../services/coupons.service";
import { QPilotAccessToken } from "../../../../core/services/qpilot.service";
import { SessionService } from "../../../../core/services/session.service";

/**
 * Import error from API
 */
interface ImportError {
  Row: number;
  ErrorMessage: string;
}

/**
 * Import dialog state
 */
type ImportState = "idle" | "uploading" | "success" | "error";

/**
 * Coupon Import Dialog Component
 * Modern drag-and-drop file upload with progress and error display
 */
@Component({
  selector: "app-import-dialog",
  standalone: true,
  imports: [
    CommonModule,
    ButtonModule,
    DialogModule,
    DividerModule,
    ProgressBarModule,
    TableModule,
    TagModule,
    TooltipModule,
  ],
  template: `
    <div class="import-dialog">
      <!-- Instructions Panel -->
      @if (state() === "idle") {
        <div class="instructions-panel">
          <div class="info-card">
            <div class="info-header">
              <i class="pi pi-info-circle"></i>
              <span>How CSV Import Works</span>
            </div>
            <div class="info-content">
              <div class="info-item">
                <i class="pi pi-plus-circle add-icon"></i>
                <div>
                  <strong>Add New Coupons:</strong>
                  <span
                    >Leave the <code>Id</code> column empty to create new
                    coupons</span
                  >
                </div>
              </div>
              <div class="info-item">
                <i class="pi pi-sync update-icon"></i>
                <div>
                  <strong>Update Existing:</strong>
                  <span
                    >Include the coupon <code>Id</code> to update existing
                    coupons</span
                  >
                </div>
              </div>
              <div class="info-item">
                <i class="pi pi-download template-icon"></i>
                <div>
                  <strong>Get Template:</strong>
                  <span
                    >Export your coupons first to get the correct CSV
                    format</span
                  >
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Drop Zone -->
        <div
          class="drop-zone"
          [class.drag-over]="isDragOver()"
          [class.has-file]="selectedFile()"
          (dragover)="onDragOver($event)"
          (dragleave)="onDragLeave($event)"
          (drop)="onDrop($event)"
          (click)="fileInput.click()"
        >
          <input
            #fileInput
            type="file"
            accept=".csv"
            (change)="onFileSelected($event)"
            class="hidden-input"
          />

          @if (!selectedFile()) {
            <div class="drop-content">
              <i class="pi pi-cloud-upload drop-icon"></i>
              <p class="drop-text">
                <span class="drop-primary"
                  >Drag and drop your CSV file here</span
                >
                <span class="drop-secondary">or click to browse</span>
              </p>
              <span class="drop-hint">Accepts .csv files only</span>
            </div>
          } @else {
            <div class="file-preview">
              <div class="file-info">
                <i class="pi pi-file file-icon"></i>
                <div class="file-details">
                  <span class="file-name">{{ selectedFile()?.name }}</span>
                  <span class="file-size">{{
                    formatFileSize(selectedFile()?.size || 0)
                  }}</span>
                </div>
              </div>
              <button
                type="button"
                class="remove-file-btn"
                (click)="removeFile($event)"
                pTooltip="Remove file"
              >
                <i class="pi pi-times"></i>
              </button>
            </div>
          }
        </div>
      }

      <!-- Uploading State -->
      @if (state() === "uploading") {
        <div class="uploading-state">
          <div class="upload-animation">
            <i class="pi pi-spin pi-spinner upload-spinner"></i>
          </div>
          <p class="upload-text">Importing coupons...</p>
          <p class="upload-subtext">Please wait while we process your file</p>
          <p-progressBar mode="indeterminate" styleClass="upload-progress" />
        </div>
      }

      <!-- Success State -->
      @if (state() === "success") {
        <div class="success-state">
          <div class="success-icon-wrapper">
            <i class="pi pi-check-circle success-icon"></i>
          </div>
          <h3 class="success-title">Import Completed!</h3>
          <p class="success-message">{{ successMessage() }}</p>

          @if (importErrors().length > 0) {
            <div class="partial-errors">
              <p-divider />
              <div class="errors-header">
                <i class="pi pi-exclamation-triangle warning-icon"></i>
                <span>{{ importErrors().length }} row(s) had issues:</span>
              </div>
              <div class="errors-table-wrapper">
                <p-table
                  [value]="importErrors()"
                  styleClass="p-datatable-sm errors-table"
                  [scrollable]="true"
                  scrollHeight="150px"
                >
                  <ng-template pTemplate="header">
                    <tr>
                      <th class="row-column">Row</th>
                      <th>Error</th>
                    </tr>
                  </ng-template>
                  <ng-template pTemplate="body" let-error>
                    <tr>
                      <td class="row-column">
                        <p-tag [value]="'Row ' + error.Row" severity="warn" />
                      </td>
                      <td class="error-message-cell">
                        {{ error.ErrorMessage }}
                      </td>
                    </tr>
                  </ng-template>
                </p-table>
              </div>
            </div>
          }
        </div>
      }

      <!-- Error State -->
      @if (state() === "error") {
        <div class="error-state">
          <div class="error-icon-wrapper">
            <i class="pi pi-times-circle error-icon"></i>
          </div>
          <h3 class="error-title">Import Failed</h3>
          <p class="error-message">{{ errorMessage() }}</p>

          @if (importErrors().length > 0) {
            <div class="errors-section">
              <p-divider />
              <div class="errors-header">
                <i class="pi pi-list"></i>
                <span>Error Details:</span>
              </div>
              <div class="errors-table-wrapper">
                <p-table
                  [value]="importErrors()"
                  styleClass="p-datatable-sm errors-table"
                  [scrollable]="true"
                  scrollHeight="200px"
                >
                  <ng-template pTemplate="header">
                    <tr>
                      <th class="row-column">Row</th>
                      <th>Error</th>
                    </tr>
                  </ng-template>
                  <ng-template pTemplate="body" let-error>
                    <tr>
                      <td class="row-column">
                        <p-tag [value]="'Row ' + error.Row" severity="danger" />
                      </td>
                      <td class="error-message-cell">
                        {{ error.ErrorMessage }}
                      </td>
                    </tr>
                  </ng-template>
                </p-table>
              </div>
            </div>
          }
        </div>
      }

      <!-- Action Buttons -->
      <div class="dialog-actions">
        @if (state() === "idle") {
          <p-button
            label="Cancel"
            severity="secondary"
            [text]="true"
            (onClick)="onCancel()"
          />
          <p-button
            label="Import"
            icon="pi pi-upload"
            [disabled]="!selectedFile()"
            (onClick)="onImport()"
          />
        }

        @if (state() === "success" || state() === "error") {
          @if (state() === "error") {
            <p-button
              label="Try Again"
              severity="secondary"
              icon="pi pi-refresh"
              (onClick)="resetState()"
            />
          }
          <p-button
            label="Close"
            [icon]="state() === 'success' ? 'pi pi-check' : 'pi pi-times'"
            (onClick)="onClose()"
          />
        }
      </div>
    </div>
  `,
  styles: [
    `
      .import-dialog {
        min-width: 480px;
        max-width: 600px;
      }

      /* Instructions Panel */
      .instructions-panel {
        margin-bottom: 1.25rem;
      }

      .info-card {
        background: linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%);
        border: 1px solid #bae6fd;
        border-radius: 12px;
        overflow: hidden;
      }

      .info-header {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        padding: 0.75rem 1rem;
        background: rgba(14, 165, 233, 0.1);
        border-bottom: 1px solid #bae6fd;
        font-weight: 600;
        font-size: 0.875rem;
        color: #0369a1;

        i {
          font-size: 1rem;
        }
      }

      .info-content {
        padding: 1rem;
        display: flex;
        flex-direction: column;
        gap: 0.75rem;
      }

      .info-item {
        display: flex;
        align-items: flex-start;
        gap: 0.75rem;
        font-size: 0.8125rem;
        color: #374151;

        i {
          font-size: 1rem;
          margin-top: 0.125rem;
          flex-shrink: 0;
        }

        .add-icon {
          color: #22c55e;
        }
        .update-icon {
          color: #3b82f6;
        }
        .template-icon {
          color: #8b5cf6;
        }

        strong {
          color: #1f2937;
          display: block;
          margin-bottom: 0.125rem;
        }

        code {
          background: #e0f2fe;
          padding: 0.125rem 0.375rem;
          border-radius: 4px;
          font-size: 0.75rem;
          color: #0369a1;
        }
      }

      /* Drop Zone */
      .drop-zone {
        border: 2px dashed #d1d5db;
        border-radius: 12px;
        padding: 2rem;
        cursor: pointer;
        transition: all 0.2s ease;
        background: #fafafa;

        &:hover {
          border-color: #3b82f6;
          background: #f8fafc;
        }

        &.drag-over {
          border-color: #3b82f6;
          background: #eff6ff;
          transform: scale(1.01);
        }

        &.has-file {
          border-style: solid;
          border-color: #22c55e;
          background: #f0fdf4;
        }
      }

      .hidden-input {
        display: none;
      }

      .drop-content {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 0.75rem;
      }

      .drop-icon {
        font-size: 3rem;
        color: #9ca3af;
      }

      .drop-text {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 0.25rem;
        margin: 0;
      }

      .drop-primary {
        font-size: 1rem;
        font-weight: 500;
        color: #374151;
      }

      .drop-secondary {
        font-size: 0.875rem;
        color: #6b7280;
      }

      .drop-hint {
        font-size: 0.75rem;
        color: #9ca3af;
      }

      /* File Preview */
      .file-preview {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 1rem;
      }

      .file-info {
        display: flex;
        align-items: center;
        gap: 0.75rem;
      }

      .file-icon {
        font-size: 2rem;
        color: #22c55e;
      }

      .file-details {
        display: flex;
        flex-direction: column;
        gap: 0.125rem;
      }

      .file-name {
        font-weight: 500;
        color: #1f2937;
        font-size: 0.9375rem;
      }

      .file-size {
        font-size: 0.75rem;
        color: #6b7280;
      }

      .remove-file-btn {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 2rem;
        height: 2rem;
        border: none;
        border-radius: 50%;
        background: #fee2e2;
        color: #ef4444;
        cursor: pointer;
        transition: all 0.15s ease;

        &:hover {
          background: #fecaca;
          transform: scale(1.1);
        }

        i {
          font-size: 0.875rem;
        }
      }

      /* Uploading State */
      .uploading-state {
        display: flex;
        flex-direction: column;
        align-items: center;
        padding: 2rem 1rem;
        text-align: center;
      }

      .upload-animation {
        margin-bottom: 1rem;
      }

      .upload-spinner {
        font-size: 3rem;
        color: #3b82f6;
      }

      .upload-text {
        font-size: 1.125rem;
        font-weight: 500;
        color: #1f2937;
        margin: 0 0 0.25rem;
      }

      .upload-subtext {
        font-size: 0.875rem;
        color: #6b7280;
        margin: 0 0 1.5rem;
      }

      ::ng-deep .upload-progress {
        width: 100%;
        height: 6px;
        border-radius: 3px;
      }

      /* Success State */
      .success-state {
        display: flex;
        flex-direction: column;
        align-items: center;
        padding: 1.5rem 1rem;
        text-align: center;
      }

      .success-icon-wrapper {
        width: 4rem;
        height: 4rem;
        border-radius: 50%;
        background: linear-gradient(135deg, #dcfce7 0%, #bbf7d0 100%);
        display: flex;
        align-items: center;
        justify-content: center;
        margin-bottom: 1rem;
      }

      .success-icon {
        font-size: 2rem;
        color: #22c55e;
      }

      .success-title {
        font-size: 1.25rem;
        font-weight: 600;
        color: #1f2937;
        margin: 0 0 0.5rem;
      }

      .success-message {
        font-size: 0.9375rem;
        color: #6b7280;
        margin: 0;
      }

      /* Error State */
      .error-state {
        display: flex;
        flex-direction: column;
        align-items: center;
        padding: 1.5rem 1rem;
        text-align: center;
      }

      .error-icon-wrapper {
        width: 4rem;
        height: 4rem;
        border-radius: 50%;
        background: linear-gradient(135deg, #fee2e2 0%, #fecaca 100%);
        display: flex;
        align-items: center;
        justify-content: center;
        margin-bottom: 1rem;
      }

      .error-icon {
        font-size: 2rem;
        color: #ef4444;
      }

      .error-title {
        font-size: 1.25rem;
        font-weight: 600;
        color: #1f2937;
        margin: 0 0 0.5rem;
      }

      .error-message {
        font-size: 0.9375rem;
        color: #6b7280;
        margin: 0;
      }

      /* Partial Errors / Errors Section */
      .partial-errors,
      .errors-section {
        width: 100%;
        margin-top: 1rem;
        text-align: left;
      }

      .errors-header {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        font-size: 0.875rem;
        font-weight: 500;
        color: #6b7280;
        margin-bottom: 0.75rem;

        .warning-icon {
          color: #f59e0b;
        }
      }

      .errors-table-wrapper {
        border: 1px solid #e5e7eb;
        border-radius: 8px;
        overflow: hidden;
      }

      ::ng-deep .errors-table {
        .p-datatable-header-row > th {
          background: #f9fafb;
          font-size: 0.75rem;
          font-weight: 600;
          padding: 0.5rem 0.75rem;
        }

        .p-datatable-tbody > tr > td {
          font-size: 0.8125rem;
          padding: 0.5rem 0.75rem;
        }

        .row-column {
          width: 100px;
          min-width: 100px;

          .p-tag {
            white-space: nowrap;
          }
        }

        .error-message-cell {
          color: #6b7280;
        }
      }

      /* Dialog Actions */
      .dialog-actions {
        display: flex;
        justify-content: flex-end;
        gap: 0.75rem;
        margin-top: 1.5rem;
        padding-top: 1rem;
        border-top: 1px solid #e5e7eb;
      }

      /* Responsive */
      @media (max-width: 576px) {
        .import-dialog {
          min-width: unset;
          width: 100%;
        }

        .info-content {
          padding: 0.75rem;
          gap: 0.5rem;
        }

        .info-item {
          font-size: 0.75rem;

          i {
            font-size: 0.875rem;
          }
        }

        .drop-zone {
          padding: 1.5rem 1rem;
        }

        .drop-icon {
          font-size: 2.5rem;
        }

        .drop-primary {
          font-size: 0.9375rem;
        }

        .drop-secondary {
          font-size: 0.8125rem;
        }

        .dialog-actions {
          flex-direction: column-reverse;

          ::ng-deep .p-button {
            width: 100%;
            justify-content: center;
          }
        }
      }
    `,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ImportDialogComponent implements OnInit, OnDestroy {
  private readonly couponsService = inject(CouponsService);
  private readonly sessionService = inject(SessionService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly destroy$ = new Subject<void>();

  // State signals
  state = signal<ImportState>("idle");
  selectedFile = signal<File | null>(null);
  isDragOver = signal(false);
  successMessage = signal("");
  errorMessage = signal("");
  importErrors = signal<ImportError[]>([]);

  constructor(
    public ref: DynamicDialogRef,
    public config: DynamicDialogConfig,
  ) {}

  get siteId(): number {
    return this.config.data?.siteId || 0;
  }

  /**
   * Get access token based on mode (embedded vs standard)
   */
  private getAccessToken(): QPilotAccessToken | undefined {
    const embeddedMode = this.config.data?.embeddedMode;
    const inputToken = this.config.data?.inputToken as
      | QPilotAccessToken
      | undefined;

    if (embeddedMode && inputToken) {
      return inputToken;
    }
    return this.sessionService.accessToken ?? undefined;
  }

  ngOnInit(): void {
    // Component initialized
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Handle drag over event
   */
  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver.set(true);
  }

  /**
   * Handle drag leave event
   */
  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver.set(false);
  }

  /**
   * Handle file drop
   */
  onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver.set(false);

    const files = event.dataTransfer?.files;
    if (files && files.length > 0) {
      const file = files[0];
      if (file.name.endsWith(".csv")) {
        this.selectedFile.set(file);
      }
    }
  }

  /**
   * Handle file input change
   */
  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      this.selectedFile.set(input.files[0]);
    }
  }

  /**
   * Remove selected file
   */
  removeFile(event: Event): void {
    event.stopPropagation();
    this.selectedFile.set(null);
  }

  /**
   * Format file size for display
   */
  formatFileSize(bytes: number): string {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  }

  /**
   * Start import
   */
  onImport(): void {
    const file = this.selectedFile();
    if (!file || !this.siteId) return;

    this.state.set("uploading");
    this.importErrors.set([]);

    const accessToken = this.getAccessToken();
    this.couponsService
      .importCoupons(this.siteId, file, accessToken)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (result: CouponImportResult) => {
          if (result.ErrorsList && result.ErrorsList.length > 0) {
            // Partial success with errors
            this.importErrors.set(result.ErrorsList);
            if (result.Message) {
              this.successMessage.set(result.Message);
              this.state.set("success");
            } else {
              this.errorMessage.set(
                "Some coupons could not be imported. See details below.",
              );
              this.state.set("error");
            }
          } else {
            // Full success
            this.successMessage.set(
              result.Message || "All coupons imported successfully!",
            );
            this.state.set("success");
          }
          this.cdr.markForCheck();
        },
        error: (error) => {
          console.error("Import error:", error);
          this.errorMessage.set(
            "Failed to import coupons. Please check your file format and try again.",
          );
          this.state.set("error");
          this.cdr.markForCheck();
        },
      });
  }

  /**
   * Reset to initial state
   */
  resetState(): void {
    this.state.set("idle");
    this.selectedFile.set(null);
    this.importErrors.set([]);
    this.errorMessage.set("");
    this.successMessage.set("");
  }

  /**
   * Cancel and close dialog
   */
  onCancel(): void {
    this.ref.close(null);
  }

  /**
   * Close dialog with result
   */
  onClose(): void {
    const wasSuccessful = this.state() === "success";
    this.ref.close({ success: wasSuccessful });
  }
}
